<template>
  <div class="interaction-chat">
    <div class="interaction-chat__head">
      <template v-if="interlocutor">
        <div :class="interlocutor.online ? '_online' : null"
             class="interaction-chat__info-avatar">
          <img v-if="interlocutor.avatar"
               :src="interlocutor.avatar"
               alt="user avatar">
        </div>
        <div class="interaction-chat__info">
          <div class="interaction-chat__info-name">
            {{ interlocutor.firstname }}
          </div>
          <div v-if="interlocutor.online"
               class="interaction-chat__info-status">
            {{ $tInteraction('Online now') }}
          </div>
          <div v-else
               class="interaction-chat__info-status">
            {{ $tInteraction('Not online now') }}
          </div>
        </div>
      </template>
      <template v-else>
        <div class="interaction-chat__title" style="opacity: 0;">-</div>
        <div class="interaction-chat__info">
          <div class="interaction-chat__info-avatar"></div>
        </div>
      </template>
    </div>
    <div class="interaction-chat__content">
      <div class="interaction-chat__history">
        <BasePreloader v-if="pending"/>
        <OverlayScrollbarsComponent ref="scrollBar"
                                    class="interaction-chat__history-scroll  os-host-flexbox"
                                    :options="scrollOptions">
          <div v-if="isMounted"
               class="interaction-chat__history-content">
            <template v-for="(item, index) in messages">
              <div :key="item.id"
                   class="interaction-chat__spacer"
                   v-if="!(messages[index - 1] && sameDate(messages[index - 1].created_at, item.created_at))">
                <span v-if="isToday(item.created_at)">{{ $tInteraction('Today') }}</span>
                <span v-else>{{ item.created_at | formatDate }}</span>
              </div>
              <InteractionChatMessage :key="`${item.id}message`"
                                      :item="item"
                                      :userId="user.id"
                                      @mounted-unread="onMessageMounted"/>
            </template>

          </div>
        </OverlayScrollbarsComponent>
      </div>

      <form @submit.prevent="send"
            class="interaction-chat__textarea">
        <textarea v-model.trim="messageText"
                  @keypress.enter="onSend"
                  :placeholder="$tInteraction('Your message...')"></textarea>
        <button type="submit"
                class="interaction-chat__textarea-btn base-btn base-btn--blue"
                :class="{'_disabled': !messageText}"
        >
          <span class="base-btn__text">{{ $tInteraction('Send a message') }}</span>
          <BasePreloader v-if="pendingSend"/>
        </button>
      </form>
    </div>

    <div class="interaction-toggle d-none d-md-flex">
      <button type="button"
              class="interaction-toggle__btn  _next-active"
              @click.prevent="onClose">
        <BaseSvg name="ico-gear-2"
                 width="1.5rem"
                 height="1.5rem"/>
        <span>{{ $tInteraction('Actions') }}</span>
      </button>
      <button type="button"
              class="interaction-toggle__btn  _active">
        <BaseSvg name="ico-msg"
                 width="1.7rem"
                 height="1.5rem"/>
        <span>{{ $tInteraction('Chat') }}</span>
      </button>
      <button type="button"
              @click.prevent="onOpenTab('showFilesMobile')"
              :class="hasNewFromServerFiles ? '_accent' :  null"
              class="interaction-toggle__btn">
        <BaseSvg name="ico-msg"
                 width="1.7rem"
                 height="1.5rem"/>
        <span>{{ $tInteraction('Files') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
  import {mapActions, mapState, mapMutations} from 'vuex'
  import {OverlayScrollbarsComponent} from 'overlayscrollbars-vue'
  import parseDateForLocal from '../../helpers/parseDateForLocal'
  import InteractionChatMessage from './InteractionChatMessage'
  import {http} from '@/axios'

  export default {
    name: 'InteractionChat',
    components: {
      OverlayScrollbarsComponent,
      InteractionChatMessage
    },
    data() {
      return {
        isMounted: false,
        scrollOptions: {
          overflowBehavior: {
            x: 'hidden',
            y: 'scroll'
          },
          callbacks: {
            onScrollStop: this.checkTop
          }
        },
        messageText: '',
        timer: null,
        prevScrollY: 0,

        // - - - -

        observer: null,
        isFocused: true,
        needToRead: []
      }
    },
    computed: {
      scrollBar() {
        return this.$refs.scrollBar && this.$refs.scrollBar.osInstance()
      },
      user() {
        return this.$store.state.user.profile
      },
      ...mapState('interaction/chat', [
        'messages',
        'interlocutor',
        'pending',
        'pendingSend',
        'timerLoadNewDuration',
      ]),
      ...mapState('interaction/files', {
        'hasNewFromServerFiles': 'hasNewFromServer',
      }),
      // messages() {
      //   let arr = []
      //   arr = this.$store.state.interaction.chat.messages
      //
      //
      //   return arr
      // }
    },
    methods: {
      ...mapMutations('interaction/chat', [
        'resetState',
      ]),
      ...mapActions('interaction/chat', [
        'stop',
        'load',
        'loadMore',
        'sendMessage',
        'subscriberOnUpdate',
        'subscriberOnUpdateRead',
        'setReadMessages',
      ]),
      onSend(event) {
        if (event.shiftKey) return
        event.preventDefault()
        this.send()
      },
      checkScroll() {
        if (!this.scrollBar) return
        const currentScroll = this.scrollBar.scroll()

        if (currentScroll.position.y > currentScroll.max.y - 10) {
          setTimeout(() => {
            this.scrollBar.scroll({y: '100%'}, 200)
          }, 50)
        }
      },
      onClose() {
        this.$emit('close')
      },
      onOpenTab(key) {
        this.$emit('open-tab', key)
      },
      sameDate(prev, next) {
        let prevDay = parseDateForLocal(prev).getDate()
        let nextDay = parseDateForLocal(next).getDate()

        return prevDay === nextDay;
      },
      isToday(date) {
        let today = new Date().getDate()
        return today === parseDateForLocal(date).getDate()
      },
      async send() {
        if (!this.messageText) return
        let sendText = this.messageText
        this.messageText = ''
        await this.sendMessage(
          {
            message: sendText,
            interaction_id: this.$route.params.id
          }
        )

        this.checkScroll()
      },
      async checkTop() {
        const currentScroll = this.scrollBar.scroll()
        const currentScrollY = currentScroll.position.y


        let oldHeight = null
        let newHeight = null

        if (currentScrollY < this.prevScrollY && currentScrollY < 50) {
          oldHeight = currentScroll.max.y
          await this.loadMore(this.$route.params.id)
          newHeight = currentScroll.max.y

          this.scrollBar.scroll({y: newHeight - oldHeight}, 0)
        }

        this.prevScrollY = currentScroll.position.y
      },

      // - - - -

      createObserver() {
        let options = {
          root: document.querySelector('#scrollArea'),
          rootMargin: '0px',
          threshold: 0.5
        }

        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              if (this.isFocused) {
                const id = Number(entry.target.getAttribute('data-id'))

                this.setReadMessages([id]).then(() => {
                  this.observer.unobserve(entry.target)
                })
              } else {
                this.needToRead.push(entry)
              }
            }
          });
        }, options);
      },

      setReadOnFocus() {
        const needToRead = this.needToRead
        if (!needToRead.length) return
        this.needToRead = []

        const ids = needToRead.map(entry => {
          return Number(entry.target.getAttribute('data-id'))
        })

        this.setReadMessages(ids).then(() => {
          needToRead.map(entry => {
            this.observer.unobserve(entry.target)
          })
        })
      },
      onMessageMounted($event) {
        this.observer.observe($event.$el)
      },

      // - - - -

      subscribeOnGlobalMessages() {
        http.post('/api/new-intereaction/set-send-notify-status', {
          interaction_id: this.$route.params.id
        })
      },

      unSubscribeOnGlobalMessages() {
        http.post('/api/new-intereaction/unset-send-notify-status', {
          interaction_id: this.$route.params.id
        })
      }
    },
    created() {
      window.onblur = () => {
        this.isFocused = false
      }

      window.onfocus = () => {
        this.isFocused = true
        this.setReadOnFocus()
      }
    },
    async mounted() {
      this.resetState()
      await this.load(this.$route.params.id)
      if (this.scrollBar) {
        this.scrollBar.scroll({y: '100%'}, 0)
      }

      this.subscriberOnUpdate()
      this.subscriberOnUpdateRead()
      this.createObserver()

      this.unSubscribeOnGlobalMessages()

      this.isMounted = true
    },
    beforeDestroy() {
      this.stop()

      this.subscribeOnGlobalMessages()
    },
    watch: {
      messages(to, from) {
        if (to.length && (!from.length || to[to.length - 1].id !== from[from.length - 1].id)) {

          this.checkScroll()
        }
      }
    }
  }
</script>

<style>

</style>
